import { observer } from "mobx-react";
import { FC, useEffect, useMemo, useState } from "react";
import { VIEW_MODE } from "src/Shared/Enums/ViewMode.enum";
import { useModal, useStore } from "src/Shared/Hooks";
import { getFormValidation } from "src/Shared/Components";
import ModalContent from "src/Shared/Components/ModalContent/ModalContent";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { IResult } from "src/Shared/Interfaces";
import { AxiosError } from "axios";
import { ToastService } from "src/Shared/Services";
import { finalize } from "rxjs";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import CompanyContactService from "../../Services/CompanyContact.service";
import CompanyOfficeService from "../../Services/CompanyOffice.service";
import {
  ICompanyContact,
  IContactNotifications,
} from "../../Interfaces/ICompanyContact";
import { ICompanyOffice } from "../../Interfaces";
import { fields } from "./fields";
import ContactNotificationPanel from "./ContactNotificationPanel";
import { TESTING_OCCASIONS } from "../../Enums/TestingOccasion.enum";
import {
  defaultNotifications,
  IMPERANS_CONTACT_ROLES,
  siteAccessLabel,
} from "../../Tools/ContactUtils";

/** The seven occasions a notification can be narrowed to (S4.3). */

type Props = {
  mode: VIEW_MODE;
  companyContact?: ICompanyContact;
  companyRowID?: string;
  primayContactId?: number;
  healthTickPrimaryContactID?: number;
  /**
   * Wizard step 4 requires an Imperans role before a contact saves; the client record allows it to
   * be left unset, where the grid reads "Not set" (S4.1/CR.5).
   */
  requireImperansRole?: boolean;
  /** Biases the address lookup to the client's own country; both TDDA countries when unknown (S3.1). */
  countryCode?: string | null;
};

const AddOrEditContact: FC<Props> = ({
  mode,
  companyContact,
  companyRowID,
  primayContactId,
  healthTickPrimaryContactID,
  requireImperansRole,
  countryCode,
}) => {
  const {
    rootStore: {
      companyContactStore: {
        refreshCompanyContactList,
        getCompanyVendorType,
        selectedCompanyVendorTypes,
      },
      companyStore: { getCompanyMenu },
      uiStore,
      commonStore: { vendorTypes },
    },
  } = useStore();

  const modal = useModal();
  const [buttonText, setbuttonText] = useState<string>("Add");
  const [tittle, settittle] = useState<string>("Add New Contact");
  const contactForm = useMemo(() => getFormValidation(fields), []);
  const getField = (key: string) => contactForm.$(key);

  // Site access and the notification block sit outside the form: the first is a list of ids where
  // empty means every site (S4.1), the second is its own panel (S4.3).
  const [siteIDs, setSiteIDs] = useState<number[]>([]);
  const [notifications, setNotifications] = useState<IContactNotifications>(
    defaultNotifications(),
  );
  const [sites, setSites] = useState<ICompanyOffice[]>([]);

  useEffect(() => {
    getCompanyVendorType(companyRowID);
    // The site list the contact's access and notification scope are chosen from (step 3's list).
    CompanyOfficeService.getCompanyOfficesList({
      pageNumber: 1,
      pageSize: 200,
      filterCollection: { companyRowID },
      sortCollection: [],
    }).subscribe({
      next: (data) => setSites(data?.results || []),
      error: () => setSites([]),
    });

    // The wizard requires a role before saving; the record does not (S4.1).
    if (requireImperansRole) getField("imperansRoleID").set("rules", "required");
    switch (mode) {
      case VIEW_MODE.ADD:
        {
        }
        break;
      case VIEW_MODE.EDIT:
        {
          setbuttonText("Update");
          settittle("Edit Contact");
        }
        break;
    }
  }, []);

  const [loading, setLoading] = useState<boolean>(false);

  const reload = () => {
    modal.close();
    refreshCompanyContactList(companyRowID);
    // Silent: refresh the sidebar count without the full-page loader.
    getCompanyMenu(companyRowID, false);
  };

  /**
   * TDDA/Health Tick options. The list is normally narrowed to the brands the client actually buys,
   * but a client created through the prospect routes has none chosen yet (that selection lives on
   * the record's Details page) — and this field is required, so narrowing to nothing would leave the
   * form unsaveable. In that case every brand is offered. A brand the contact already holds is
   * always kept, so editing an older contact never shows a blank value.
   */
  const vendorTypeOptions = () => {
    const held: number[] = getField("vendorTypeID").value || [];
    if (!selectedCompanyVendorTypes.length) return vendorTypes;
    return vendorTypes.filter(
      (x) => selectedCompanyVendorTypes.includes(x.value) || held.includes(x.value),
    );
  };

  useEffect(() => {
    if (companyContact && mode == VIEW_MODE.EDIT) {
      contactForm.set(companyContact);
      setSiteIDs(companyContact.siteIDs || []);
      // Backfilled to the documented defaults for a contact stored before the block existed.
      setNotifications(companyContact.notifications || defaultNotifications());
    }
  }, [companyContact]);

  const gridPropsForContacts: IGridFormControlProps = {
    row: 1,
    col: 8,
    itemResponsive: {
      lg: 12,
      sm: 12,
      md: 4,
    },
    inputControls: [
      // First and last name are stored separately (decision 15); the joined name is maintained
      // server-side so every existing reader of a contact's name keeps working.
      {
        field: getField("firstName"),
        fieldKey: "firstName",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("lastName"),
        fieldKey: "lastName",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("emailAddress"),
        fieldKey: "emailAddress",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("imperansRoleID"),
        fieldKey: "imperansRoleID",
        type: EDITOR_TYPES.DROPDOWN,
        options: IMPERANS_CONTACT_ROLES,
      },
      {
        field: getField("jobTitle"),
        fieldKey: "jobTitle",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("phoneNumber"),
        fieldKey: "phoneNumber",
        type: EDITOR_TYPES.Phone_Number,
        inputProps: {
          ristrictCountry: ["nz", "au"],
        },
      },
      {
        field: getField("officeNumber"),
        fieldKey: "officeNumber",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        // The same Google-backed lookup as the site address, for the same reason: an address typed
        // free-hand arrives in as many shapes as there are people typing it, and this one is read by
        // the setup team off the covering email. Free text is still accepted — the lookup fills the
        // field in, it does not police it.
        field: getField("staffPrimaryOfficeAddress"),
        fieldKey: "staffPrimaryOfficeAddress",
        type: EDITOR_TYPES.ADDRESS_LOOKUP,
        inputProps: { countryCode },
      },
      {
        field: getField("vendorTypeID"),
        fieldKey: "vendorTypeID",
        type: EDITOR_TYPES.DROPDOWN,
        options: vendorTypeOptions(),
        multiple: true,
      },
      {
        // Visible for every contact, matching the other four flags — Main used to be offered only
        // to whichever contact already held it, so it could only ever be moved from the grid's own
        // radio (task 5851). The singleton is enforced server-side (EnsureSingleMainContact, S4.2)
        // regardless of which surface set it, so exposing it here needs no extra guard.
        field: getField("isPrimaryContact"),
        fieldKey: "isPrimaryContact",
        type: EDITOR_TYPES.SWITCH,
      },
      {
        field: getField("isRequestingTestPersonnel"),
        fieldKey: "isRequestingTestPersonnel",
        type: EDITOR_TYPES.SWITCH,
      },
      {
        field: getField("hasResultsAccess"),
        fieldKey: "hasResultsAccess",
        type: EDITOR_TYPES.SWITCH,
      },
      // Settable here as well as from the grid, so the form can never read as though several
      // contacts could hold Main (S4.6).
      {
        field: getField("isPointOfContact"),
        fieldKey: "isPointOfContact",
        type: EDITOR_TYPES.SWITCH,
      },
      {
        field: getField("isSafetyCritical"),
        fieldKey: "isSafetyCritical",
        type: EDITOR_TYPES.SWITCH,
      },
    ],
    form: contactForm,
  };

  const onFromSubmit = () => {
    if (!contactForm.isValid) {
      contactForm.showErrors();
      return;
    }
    const request: ICompanyContact = {
      ...contactForm.values(),
      siteIDs,
      notifications,
    };
    setLoading(true);
    switch (mode) {
      case VIEW_MODE.ADD:
        request.companyRowID = companyRowID;
        uiStore.setGlobalLoader(true);
        var companyContact: ICompanyContact[] = [request];
        CompanyContactService.addCompanyContactList(companyContact)
          .pipe(
            finalize(() => {
              setLoading(false);
              uiStore.setGlobalLoader(false);
            }),
          )
          .subscribe({
            next: (data: IResult<string>) => {
              ToastService.success(data.message);
              reload();
            },
            error: (error: AxiosError) => ToastService.axiosError(error),
          });
        break;
      case VIEW_MODE.EDIT:
        uiStore.setGlobalLoader(true);
        CompanyContactService.updateompanyContact(request)
          .pipe(
            finalize(() => {
              setLoading(false);
              uiStore.setGlobalLoader(false);
            }),
          )
          .subscribe({
            next: (data: IResult<string>) => {
              ToastService.success(data.message);
              reload();
            },
            error: (error: AxiosError) => ToastService.axiosError(error),
          });
        break;
    }
  };

  return (
    <ModalContent
      onSaveButtonClick={onFromSubmit}
      buttonText={buttonText}
      title={tittle}
      btnLoading={loading}
    >
      <GridFormControl {...gridPropsForContacts} />

      {/* Site access: nothing ticked means every site, including sites added later (S4.1). */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Site access — leave empty for all sites
        </Typography>
        {sites.length === 0 ? (
          <Typography variant="body2">
            No sites yet. This contact will cover every site added later.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {sites.map((site) => (
              <FormControlLabel
                key={site.companySiteID}
                control={
                  <Checkbox
                    size="small"
                    checked={siteIDs.includes(site.companySiteID)}
                    onChange={(e) =>
                      setSiteIDs(
                        e.target.checked
                          ? [...siteIDs, site.companySiteID]
                          : siteIDs.filter((x) => x !== site.companySiteID),
                      )
                    }
                  />
                }
                label={site.officeName}
              />
            ))}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">
          {siteAccessLabel(siteIDs, sites)}
        </Typography>
      </Box>

      {/* No panel without the grant; removing it hides the panel without discarding the settings. */}
      {getField("hasResultsAccess").value && (
        <ContactNotificationPanel
          notifications={notifications}
          onChange={(changes) => setNotifications({ ...notifications, ...changes })}
          occasions={TESTING_OCCASIONS}
          sites={sites}
        />
      )}

      {getField("isPointOfContact").value && !getField("hasResultsAccess").value && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          A point of contact does not receive results unless they are also given results access.
        </Typography>
      )}
    </ModalContent>
  );
};

export default observer(AddOrEditContact);
